import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CalendarDays, Clock, Scissors, User, CalendarPlus } from "lucide-react";

const STATUS = {
  confirmed: { label: "Confirmado", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  completed: { label: "Concluído", cls: "bg-green-500/15 text-green-400 border-green-500/30" },
  cancelled: { label: "Cancelado", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

export default function MyAppointments() {
  const nav = useNavigate();
  const [settings, setSettings] = useState({});
  const [items, setItems] = useState([]);

  const load = () => api.get("/appointments/mine").then((r) => setItems(r.data || []));
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})); load(); }, []);

  const cancel = async (id) => {
    try { await api.delete(`/appointments/${id}`); toast.success("Agendamento cancelado"); load(); }
    catch { toast.error("Erro ao cancelar"); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <Navbar logo={settings.logo} shopName={settings.shop_name || "Big Black"} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">Meus Agendamentos</h1>
            <p className="text-zinc-400 mt-1">Gerencie seus horários na Big Black.</p>
          </div>
          <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" onClick={() => nav("/agendar")} data-testid="new-booking-btn">
            <CalendarPlus className="w-4 h-4 mr-2" /> Novo
          </Button>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900 border border-zinc-800 rounded-2xl">
            <CalendarDays className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Você ainda não tem agendamentos.</p>
            <Button className="mt-6 bg-amber-400 text-black font-bold rounded-full" onClick={() => nav("/agendar")}>Agendar agora</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((a) => {
              const st = STATUS[a.status] || STATUS.confirmed;
              const canManage = a.status === "confirmed";
              return (
                <div key={a.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`appointment-${a.id}`}>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2"><Scissors className="w-4 h-4 text-amber-400" />{a.service_name}</h3>
                      <Badge className={`border ${st.cls}`}>{st.label}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-zinc-400">
                      <span className="flex items-center gap-1"><User className="w-4 h-4" />{a.barber_name}</span>
                      <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{new Date(a.date + "T00:00:00").toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" })}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{a.start_time} - {a.end_time}</span>
                      <span className="text-amber-400 font-semibold">{money(a.price)}</span>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex gap-2">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`cancel-${a.id}`}>Cancelar</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">Cancelar agendamento?</AlertDialogTitle>
                            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white">Voltar</AlertDialogCancel>
                            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => cancel(a.id)} data-testid={`confirm-cancel-${a.id}`}>Cancelar horário</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
